import React, { useEffect, useRef, useState } from 'react';

import EmailEditor, { EditorRef, EmailEditorProps } from 'react-email-editor';

import Form from '../../components/common/Form';
import FormField from '../../components/common/FormField';
import FormSection from '../../components/common/FormSection';
import { BASE_URL, UNLAYER_PROJECT_ID } from '../../config/config';
import { FileTypeConstants } from '../../constants/FileTypeConstants';
import { useContentCategoryApi } from '../../hooks/useContentCategoryApi';
import { useFileApi } from '../../hooks/useFileApi';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import { buildArticleUrl } from '../../modules/infoHub';
import { ArticleDTO, ContentCategoryDTO } from '../../services/apiClient';
import { formatDateForInput } from '../../utils/dateUtils/formatDateForInput';
import handleChange from '../../utils/formUtils/handleChange';
import { slugify } from '../../utils/slugify';
import FileUpload from '../common/FileUpload';
import ImageLink from '../common/ImageLink';

interface ArticleFormProps {
  initialValues: ArticleDTO;
  onSubmit: (data: ArticleDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  picture: {
    id: 'picture',
    title: 'Picture',
  },
  content: {
    id: 'content',
    title: 'Content',
  },
} as const;

const ArticleForm: React.FC<ArticleFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<ArticleDTO>(initialValues);
  const [contentCategories, setContentCategories] = useState<ContentCategoryDTO[]>([]);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const emailEditorRef = useRef<EditorRef>(null);

  const { uploadFile } = useFileApi();
  const { search: searchContentCategories } = useContentCategoryApi();

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
    date: [
      { required: true, message: 'Date is required' },
      { custom: (value) => !isNaN(Date.parse(value)), message: 'Date must be a valid date' },
    ],
    externalUrl: [
      {
        custom: (value) => !value || /^https?:\/\//i.test(value),
        message: 'External URL must start with http:// or https://',
      },
    ],
  };

  const { errors, validate } = useFormValidation<ArticleDTO>(validationRules);

  useEffect(() => {
    const loadContentCategories = async () => {
      const response = await searchContentCategories({
        filters: { isActive: true },
        page: 1,
        pageSize: 500,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      setContentCategories(response?.data?.results || []);
    };
    loadContentCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Slug is derived from the Title and shown read-only — the admin only types a Title,
  // mirroring the Pages and Newsletter forms.
  useEffect(() => {
    setFormData((prev) => {
      const slug = slugify(prev.title || '');
      return prev.slug === slug ? prev : { ...prev, slug };
    });
  }, [formData.title]);

  const handlePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      setUploadingPicture(true);
      const response = await uploadFile('files', file.name, file, 'articles');
      if (response?.success && response.data) {
        setFormData((prev) => ({ ...prev, pictureFileRecordId: response.data }));
      }
    } catch (error) {
      console.error('Error uploading picture:', error);
    } finally {
      setUploadingPicture(false);
    }
  };

  // Load the saved Unlayer design (article.body) into the editor once it is ready.
  const onEditorReady: EmailEditorProps['onReady'] = (unlayer) => {
    if (initialValues.body) {
      try {
        unlayer.loadDesign(JSON.parse(initialValues.body));
      } catch (e) {
        console.error('Failed to parse saved article design JSON:', e);
      }
    }
  };

  const handleSave = () => {
    if (!validate(formData)) {
      return;
    }

    const slug = slugify(formData.title || '');
    const editor = emailEditorRef.current?.editor;
    if (!editor) {
      onSubmit({ ...formData, slug });
      return;
    }

    // Export both the rendered HTML (stored inline on `content`) and the design JSON
    // (stored on `body` so the editor can reopen it via loadDesign).
    editor.exportHtml((data) => {
      const { design, html } = data;
      onSubmit({
        ...formData,
        slug,
        content: html,
        body: JSON.stringify(design),
      });
    });
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel} isSubmitting={isLoading}>
      <FormSection id={FORM_SECTIONS.basicInfo.id} title={FORM_SECTIONS.basicInfo.title}>
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required
          error={errors.title}
        />
        <FormField
          id="contentCategoryId"
          label="Category"
          type="dropdown"
          value={formData.contentCategoryId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select a category"
          searchable
          error={errors.contentCategoryId}
          options={contentCategories.map((category) => ({
            value: category.id,
            label: category.title || '',
          }))}
          helper="Categories turn a set of articles into a series."
        />
        <FormField
          id="slug"
          label="Title URL"
          type="text"
          value={formData.slug || ''}
          onChange={() => {}}
          disabled
          helper="Automatically generated from the title."
        />
        <FormField
          id="articleUrl"
          label="Path URL"
          type="text"
          value={buildArticleUrl(BASE_URL, formData.slug)}
          onChange={() => {}}
          disabled
          helper="The public URL for this article."
        />
        <FormField
          id="date"
          label="Date"
          type="date"
          value={formatDateForInput(formData.date)}
          onChange={(e) => handleChange(e, setFormData)}
          required
          error={errors.date}
        />
        <FormField
          id="isActive"
          label="Active"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          error={errors.isActive}
          helper="Check to make this article active."
        />
        <FormField
          id="externalUrl"
          label="External URL"
          type="text"
          value={formData.externalUrl || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="https://..."
          error={errors.externalUrl}
          helper="Leave blank to publish the content below. Fill this in to link out to an article hosted elsewhere."
        />
        <FormField
          id="summary"
          label="Summary"
          type="textarea"
          value={formData.summary || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter a short summary"
          maxLength={500}
          error={errors.summary}
          helper="Shown on the archive card. Falls back to the content when left blank."
        />
      </FormSection>

      <FormSection id={FORM_SECTIONS.picture.id} title={FORM_SECTIONS.picture.title} columns={1}>
        {formData.pictureFileRecordId && (
          <ImageLink fileId={formData.pictureFileRecordId} label="Current Picture" />
        )}
        <FileUpload
          id="pictureFileRecordId"
          title="Upload or drag and drop the article picture"
          accept={FileTypeConstants.IMAGES}
          onChange={handlePictureChange}
          isUploading={uploadingPicture}
          error={errors.pictureFileRecordId}
        />
      </FormSection>

      <FormSection id={FORM_SECTIONS.content.id} title={FORM_SECTIONS.content.title} columns={1}>
        <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
          Design the article below. On save the rendered HTML is stored along with the design, so it
          can be edited again later. Ignored when an External URL is set.
        </p>
        <div className="w-full">
          <EmailEditor
            ref={emailEditorRef}
            onReady={onEditorReady}
            minHeight="700px"
            style={{ width: '100%' }}
            options={{
              projectId: UNLAYER_PROJECT_ID,
              displayMode: 'web',
            }}
          />
        </div>
      </FormSection>
    </Form>
  );
};

export default ArticleForm;
